import { format } from 'date-fns';
import { map, pipe } from 'remeda';
import {
  formatAdminDate,
  getEffectiveSubscriptionStatus,
  getSubscriptionStatusLabel,
  type EnrichedAdminUser,
} from './adminUsersTable.utils';

export const ADMIN_USERS_CSV_HEADERS = [
  { label: 'ID utente', key: 'id' },
  { label: 'Email', key: 'email' },
  { label: 'Nome visualizzato', key: 'displayName' },
  { label: 'Nome', key: 'firstName' },
  { label: 'Cognome', key: 'lastName' },
  { label: 'Nome completo', key: 'fullName' },
  { label: 'Ruolo', key: 'role' },
  { label: 'Tipo account', key: 'accountType' },
  { label: 'Titolo', key: 'title' },
  { label: 'Professione', key: 'profession' },
  { label: 'Disciplina', key: 'disciple' },
  { label: 'Contratto', key: 'contract' },
  { label: 'Ordine psicologi', key: 'orderOfPsychologists' },
  { label: 'Numero ordine', key: 'orderNumber' },
  { label: 'Profilo pubblico', key: 'isPublic' },
  { label: 'Enterprise', key: 'belongsToEnterprise' },
  { label: 'Website', key: 'website' },
  { label: 'Regione profilo', key: 'profileRegion' },
  { label: 'Indirizzo profilo', key: 'profileAddress' },
  { label: 'Codice fiscale', key: 'fiscalCode' },
  { label: 'Partita IVA', key: 'vatNumber' },
  { label: 'Codice SDI', key: 'sdiNumber' },
  { label: 'Stripe ID', key: 'stripeId' },
  { label: 'Stato subscription tabella', key: 'subscriptionVisibilityStatus' },
  { label: 'Subscription primaria', key: 'primarySubscriptionName' },
  { label: 'Subscription primaria stato', key: 'primarySubscriptionStatus' },
  {
    label: 'Subscription primaria categoria',
    key: 'primarySubscriptionCategory',
  },
  { label: 'Subscription primaria inizio', key: 'primarySubscriptionStart' },
  { label: 'Subscription primaria fine', key: 'primarySubscriptionEnd' },
  { label: 'Totale subscription', key: 'subscriptionCount' },
  { label: 'Subscription attive', key: 'activeSubscriptionsCount' },
  { label: 'Subscription in scadenza', key: 'expiringSoonSubscriptionsCount' },
  { label: 'Subscription scadute', key: 'expiredSubscriptionsCount' },
  { label: 'Nomi subscription', key: 'subscriptionNames' },
  { label: 'Dettaglio subscription', key: 'subscriptionDetails' },
  { label: 'Geolocalizzazione', key: 'geolocationLabel' },
  { label: 'Citta', key: 'geolocationCity' },
  { label: 'Regione geolocalizzazione', key: 'geolocationRegion' },
  { label: 'Paese', key: 'geolocationCountry' },
  { label: 'Timezone', key: 'geolocationTimezone' },
  { label: 'IP', key: 'geolocationIpAddress' },
  { label: 'Latitudine', key: 'geolocationLatitude' },
  { label: 'Longitudine', key: 'geolocationLongitude' },
  { label: 'Legacy source', key: 'legacySource' },
  { label: 'Devices', key: 'devicesCount' },
  { label: 'Corsi', key: 'coursesCount' },
  { label: 'Creato il', key: 'createdAt' },
  { label: 'Aggiornato il', key: 'updatedAt' },
] as const;

type AdminUsersCsvRow = Record<
  (typeof ADMIN_USERS_CSV_HEADERS)[number]['key'],
  string | number
>;

const EMPTY_VALUE = '-';

const formatCsvDateTime = (value: string | Date | null | undefined): string => {
  if (!value) {
    return EMPTY_VALUE;
  }

  const parsedDate = new Date(value);
  if (Number.isNaN(parsedDate.getTime())) {
    return EMPTY_VALUE;
  }

  return format(parsedDate, 'dd/MM/yyyy HH:mm');
};

const formatCsvValue = (value: string | number | null | undefined): string =>
  value === null || value === undefined || value === ''
    ? EMPTY_VALUE
    : String(value);

const formatCsvFlag = (value: number | null | undefined): string => {
  if (value === 1) {
    return 'Si';
  }

  if (value === 0) {
    return 'No';
  }

  return EMPTY_VALUE;
};

const formatSubscriptionDetail = (user: EnrichedAdminUser): string => {
  if (user.normalizedSubscriptions.length === 0) {
    return EMPTY_VALUE;
  }

  return user.normalizedSubscriptions
    .map(subscription => {
      const effectiveStatus = getEffectiveSubscriptionStatus(subscription);

      return [
        subscription.name,
        getSubscriptionStatusLabel(effectiveStatus),
        `${formatAdminDate(subscription.startDate)} -> ${formatAdminDate(
          subscription.endDate
        )}`,
        subscription.category ? `Categoria: ${subscription.category}` : null,
        subscription.source ? `Fonte: ${subscription.source}` : null,
        subscription.description ? `Info: ${subscription.description}` : null,
      ]
        .filter(Boolean)
        .join(' | ');
    })
    .join('; ');
};

const countSubscriptionsByStatus = (
  user: EnrichedAdminUser,
  status: 'active' | 'expiringSoon' | 'expired'
): number =>
  user.normalizedSubscriptions.filter(
    subscription => getEffectiveSubscriptionStatus(subscription) === status
  ).length;

const createAdminUsersCsvRow = (user: EnrichedAdminUser): AdminUsersCsvRow => ({
  id: user.id,
  email: formatCsvValue(user.email),
  displayName: formatCsvValue(user.displayName),
  firstName: formatCsvValue(user.first_name),
  lastName: formatCsvValue(user.last_name),
  fullName: formatCsvValue(user.full_name),
  role: formatCsvValue(user.role),
  accountType: formatCsvValue(user.type),
  title: formatCsvValue(user.title),
  profession: formatCsvValue(user.profession),
  disciple: formatCsvValue(user.disciple),
  contract: formatCsvValue(user.contract),
  orderOfPsychologists: formatCsvValue(user.order_of_psychologists),
  orderNumber: formatCsvValue(user.order_number),
  isPublic: formatCsvFlag(user.is_public),
  belongsToEnterprise: formatCsvFlag(user.belongs_to_enterprise),
  website: formatCsvValue(user.website),
  profileRegion: formatCsvValue(user.region),
  profileAddress: formatCsvValue(user.address),
  fiscalCode: formatCsvValue(user.fiscal_code),
  vatNumber: formatCsvValue(user.vat_number),
  sdiNumber: formatCsvValue(user.sdi_number),
  stripeId: formatCsvValue(user.stripe_id),
  subscriptionVisibilityStatus: getSubscriptionStatusLabel(
    user.subscriptionVisibilityStatus
  ),
  primarySubscriptionName: formatCsvValue(user.primarySubscription?.name),
  primarySubscriptionStatus: user.primarySubscription
    ? getSubscriptionStatusLabel(
        getEffectiveSubscriptionStatus(user.primarySubscription)
      )
    : EMPTY_VALUE,
  primarySubscriptionCategory: formatCsvValue(
    user.primarySubscription?.category
  ),
  primarySubscriptionStart: formatAdminDate(
    user.primarySubscription?.startDate
  ),
  primarySubscriptionEnd: formatAdminDate(user.primarySubscription?.endDate),
  subscriptionCount: user.normalizedSubscriptions.length,
  activeSubscriptionsCount: countSubscriptionsByStatus(user, 'active'),
  expiringSoonSubscriptionsCount: countSubscriptionsByStatus(
    user,
    'expiringSoon'
  ),
  expiredSubscriptionsCount: countSubscriptionsByStatus(user, 'expired'),
  subscriptionNames:
    user.subscriptionTypes.length > 0
      ? user.subscriptionTypes.join('; ')
      : EMPTY_VALUE,
  subscriptionDetails: formatSubscriptionDetail(user),
  geolocationLabel: formatCsvValue(user.geolocationSummary?.label),
  geolocationCity: formatCsvValue(user.geolocationSummary?.city),
  geolocationRegion: formatCsvValue(user.geolocationSummary?.region),
  geolocationCountry: formatCsvValue(user.geolocationSummary?.country),
  geolocationTimezone: formatCsvValue(user.geolocationSummary?.timezone),
  geolocationIpAddress: formatCsvValue(user.geolocationSummary?.ipAddress),
  geolocationLatitude: formatCsvValue(user.geolocationSummary?.latitude),
  geolocationLongitude: formatCsvValue(user.geolocationSummary?.longitude),
  legacySource: formatCsvValue(user.legacy_source),
  devicesCount: user.devicesCount,
  coursesCount: user.coursesCount,
  createdAt: formatCsvDateTime(user.created_at),
  updatedAt: formatCsvDateTime(user.updated_at),
});

export const createAdminUsersCsvData = (
  users: EnrichedAdminUser[]
): AdminUsersCsvRow[] =>
  pipe(
    users,
    map(user => createAdminUsersCsvRow(user))
  );
